// src/features/Users/ShiftEventsTable.jsx
import React, { useEffect, useState } from "react";
import {Table, Typography, Tag, message, Avatar, Image, Alert} from "antd";
import { apiRequests } from "src/shared/api/api";
import { ClockCircleOutlined } from "@ant-design/icons";

const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const shiftStatusLabels = {
  on_shift: "На смене",
  off_shift: "Смена завершена",
  on_break: "На перерыве",
};

const shiftStatusColors = {
  on_shift: "green",
  off_shift: "red",
  on_break: "orange",
};

const ShiftEventsTable = ({ websocket }) => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });

  const fetchEvents = (page = 1, pageSize = pagination.pageSize) => {
    setLoading(true);
    const offset = (page - 1) * pageSize;
    apiRequests.shifts
      .getShiftEvents(pageSize, offset)
      .then((response) => {
        const data = response.data.result || [];
        const total = response.data.total_count ?? response.data.count ?? 0;
        setDataSource(data);
        setPagination({
          current: page,
          pageSize,
          total,
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error("Ошибка загрузки событий смен:", err);
        message.error("Не удалось загрузить события смен");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchEvents(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!websocket) return;

    const handleMessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.target === "employee_shifts" && data.action) {
        fetchEvents(pagination.current, pagination.pageSize);
      }
    };

    websocket.addEventListener("message", handleMessage);
    return () => {
      websocket.removeEventListener("message", handleMessage);
    }; // eslint-disable-next-line
  }, [websocket, pagination.current, pagination.pageSize]);

  const handleTableChange = (newPagination /*, filters, sorter */) => {
    fetchEvents(newPagination.current, newPagination.pageSize);
  };

  const columns = [
    {
      title: "Фото",
      dataIndex: "photo",
      key: "photo",
      width: 80,
      render: (photo_src) => (
        <Avatar
          src={<Image src={`https://${process.env.REACT_APP_APP_URL}/${photo_src}`} />}
          size={60}
        />
      ),
    },
    {
      title: "Пользователь",
      key: "user",
      render: (_, record) => (
        <Typography.Text>
          {record.first_name} {record.last_name || ""}
        </Typography.Text>
      ),
    },
    {
      title: "Ссылка ТГ",
      key: "username",
      dataIndex: "username",
      width: 200,
      render: (username) =>
        username ? (
          <a target={"_blank"} rel="noreferrer" href={`https://t.me/${username}`}>
            https://t.me/{username}
          </a>
        ) : (
          <Alert message="У этого пользователя нет юзернейма" type="warning" />
        ),
    },
    {
      title: "Статус смены",
      dataIndex: "shift_status",
      key: "shift_status",
      render: (status) => (
        <Tag color={shiftStatusColors[status] || "default"}>
          {shiftStatusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: "Время события",
      dataIndex: "event_start",
      key: "event_start",
      render: (date) => (
        <div style={{ whiteSpace: "nowrap" }}>
          <ClockCircleOutlined style={{ marginRight: 6, color: "#1890ff" }} />
          {formatDateTime(date)}
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey={(record) => `${record.relation_id}-${record.event_start}`}
      loading={loading}
      dataSource={dataSource}
      columns={columns}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["25", "50", "100"],
      }}
      onChange={handleTableChange}
      bordered
    />
  );
};

export default ShiftEventsTable;
